import { useLayoutEffect, useMemo, useRef, useState } from "react"

import { Braces } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import {
  buildTemplateSuggestions,
  insertTemplateToken,
  shouldOpenTemplateSuggestions,
  type TemplateSuggestion,
} from "./template-field-utils"
import { useWorkflowStore } from "../../stores/workflow-store"
import { resolveStartFields } from "../../utils/workflow-run-utils"

type TemplateFieldProps = {
  value: string
  placeholder?: string
  className?: string
  multiline?: boolean
  menuLabel?: string
  allowLoopTokens?: boolean
  onChange: (value: string) => void
}

export function TemplateField({
  value,
  placeholder,
  className,
  multiline = false,
  menuLabel = "Available placeholders",
  allowLoopTokens = true,
  onChange,
}: TemplateFieldProps) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  // The Popover's Anchor wrapper (input/textarea + Braces toggle). Radix's
  // non-modal dismiss logic only exempts clicks that land on the trigger it
  // knows about (context.triggerRef, set by PopoverTrigger) — an Anchor never
  // populates that ref. Without this, every pointerdown inside the field
  // (repositioning the caret) or on the toggle button gets treated as an
  // "outside" interaction and closes the popover before the field's own
  // handlers run. onInteractOutside below checks against this ref instead.
  const anchorRef = useRef<HTMLDivElement | null>(null)
  // Last known caret/selection while the field is focused. A controlled
  // input/textarea resets the caret to the end whenever its value prop changes —
  // and that can happen on a re-render *after* the keystroke (e.g. an
  // autosave-triggered store update), not only on the keystroke itself. Tracking
  // the selection on every change and re-applying it after each render keeps the
  // caret put while editing mid-text.
  const caretRef = useRef<{ start: number; end: number } | null>(null)
  // True while an IME composition is in progress (e.g. CJK input, dead-key
  // accents). Forcing the selection mid-composition cancels/corrupts the
  // composing text, so the caret restore is suppressed until composition ends.
  const isComposingRef = useRef(false)
  const [open, setOpen] = useState(false)
  const nodes = useWorkflowStore((state) => state.nodes)
  const variableNames = useMemo(
    () =>
      nodes
        .filter((node) => node.type === "variable")
        .map((node) => node.data.variableName)
        .filter((name): name is string => typeof name === "string" && name.trim().length > 0),
    [nodes]
  )
  // Surface the START node's TEXT input-field keys (e.g. text_0) so authors can
  // insert {text_0} etc. The start node's data carries the same shape that
  // resolveStartFields consumes (fields[] or legacy requiresInput/fileMode), so
  // legacy/non-multi-field flows resolve to the same keys they always have.
  const startFieldKeys = useMemo(() => {
    const startNode = nodes.find((node) => node.type === "start")
    // startNode.data is xyflow's Record<string, unknown>; resolveStartFields
    // validates every property defensively, matching the existing call sites
    // (e.g. workflow-input-dialog) that bridge the same untyped node data.
    return resolveStartFields(startNode?.data as never)
      .filter((field) => field.type === "text")
      .map((field) => field.key)
  }, [nodes])
  const suggestions = useMemo(
    () =>
      buildTemplateSuggestions(variableNames, {
        includeLoopTokens: allowLoopTokens,
        startFieldKeys,
      }),
    [variableNames, allowLoopTokens, startFieldKeys]
  )

  const rememberCaret = (element: HTMLInputElement | HTMLTextAreaElement) => {
    caretRef.current = { start: element.selectionStart ?? 0, end: element.selectionEnd ?? 0 }
  }

  // After every render, re-apply the tracked selection while the field is
  // focused. Setting `value` on a controlled element silently moves the caret to
  // the end (it fires no select event), so this runs synchronously before paint
  // to put it back wherever the user actually is. Selection changes the user
  // makes (typing, clicking, arrow keys) update caretRef via onSelect first, so
  // this never fights real cursor movement. caretRef is cleared on blur, so a
  // refocus won't reapply a stale selection; and the restore is skipped during
  // IME composition to avoid interrupting the composing text.
  useLayoutEffect(() => {
    const element = inputRef.current
    const caret = caretRef.current
    if (!element || !caret || isComposingRef.current || document.activeElement !== element) return
    if (element.selectionStart !== caret.start || element.selectionEnd !== caret.end) {
      element.setSelectionRange(caret.start, caret.end)
    }
  })

  const insertToken = (suggestion: TemplateSuggestion) => {
    const element = inputRef.current
    const insertion = insertTemplateToken(value, suggestion.token, {
      start: element?.selectionStart ?? null,
      end: element?.selectionEnd ?? null,
    })
    caretRef.current = { start: insertion.cursor, end: insertion.cursor }
    onChange(insertion.value)
    setOpen(false)
    window.requestAnimationFrame(() => {
      element?.focus()
      element?.setSelectionRange(insertion.cursor, insertion.cursor)
    })
  }

  const sharedProps = {
    className: cn("nodrag nowheel text-xs", className),
    placeholder,
    value,
    onBlur: () => {
      // Drop the tracked caret so a later refocus doesn't reapply a stale
      // selection — the browser's natural placement (and the next onSelect)
      // should win on refocus.
      caretRef.current = null
      window.setTimeout(() => setOpen(false), 120)
    },
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const el = event.target
      // While composing, let the IME own the caret; don't record/restore it.
      if (!isComposingRef.current) rememberCaret(el)
      onChange(el.value)
      setOpen(shouldOpenTemplateSuggestions(el.value, el.selectionStart))
    },
    onCompositionStart: () => {
      isComposingRef.current = true
    },
    onCompositionEnd: (event: React.CompositionEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      isComposingRef.current = false
      // Composition committed — record where the caret landed so subsequent
      // value-driven re-renders restore to the right place.
      rememberCaret(event.currentTarget)
    },
    onSelect: (event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!isComposingRef.current) rememberCaret(event.currentTarget)
    },
    onKeyUp: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setOpen(
        shouldOpenTemplateSuggestions(event.currentTarget.value, event.currentTarget.selectionStart)
      )
    },
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className="relative">
          {multiline ? (
            <Textarea
              ref={(node) => {
                inputRef.current = node
              }}
              {...sharedProps}
            />
          ) : (
            <Input
              ref={(node) => {
                inputRef.current = node
              }}
              {...sharedProps}
            />
          )}
          <PopoverTrigger asChild>
            <Button
              aria-label="Insert template variable"
              className="nodrag absolute top-1 right-1 size-6 p-0"
              size="icon"
              title="Insert template variable"
              type="button"
              variant="ghost"
            >
              <Braces className="size-3.5" />
            </Button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="z-[120] w-[min(24rem,calc(100vw-2rem))] p-1"
        onCloseAutoFocus={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          // Clicks inside the anchor (repositioning the caret in the
          // input/textarea, or the Braces toggle button) aren't "outside" —
          // let PopoverTrigger's own click handling and the field's normal
          // focus/typing behavior own those instead of Radix's dismiss logic.
          if (anchorRef.current?.contains(event.target as Node)) {
            event.preventDefault()
          }
        }}
      >
        <div className="border-b px-2 py-1.5">
          <div className="text-xs font-medium">{menuLabel}</div>
          <div className="text-muted-foreground text-[10px]">
            Type {"{"} or click an option to insert dynamic data.
          </div>
        </div>
        {suggestions.map((suggestion) => (
          <button
            key={suggestion.token}
            className="hover:bg-muted grid w-full grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 rounded px-2 py-1.5 text-left text-xs"
            type="button"
            onMouseDown={(event) => {
              event.preventDefault()
              insertToken(suggestion)
            }}
          >
            <span className="font-medium">{suggestion.label}</span>
            <code className="bg-muted rounded px-1 py-0.5 font-mono text-[10px]">
              {suggestion.token}
            </code>
            <span className="text-muted-foreground col-span-2 text-[10px]">
              {suggestion.description}
            </span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}
